'use client';

/**
 * Signed in, or not — and the credentials this browser keeps so it never has to
 * ask twice.
 *
 * ── The promise ─────────────────────────────────────────────────────────────
 * The login page says, in as many words, that signing in happens once and this
 * device will not be asked again. Two separate mechanisms keep that promise, and
 * both are needed:
 *
 *   1. The `gt_auth` cookie — HttpOnly, one year, minted by the server. This is
 *      the one that does the work on every ordinary visit.
 *   2. The TOKEN the server issued at sign-in, kept in `localStorage` and
 *      presented when the cookie is gone (lib/api.ts). Never the password —
 *      until 2026-10-12 it was the nickname and password, and every browser
 *      that still has that copy trades it for a token on its next visit.
 *
 * Without (2) the promise quietly breaks in all the ways a cookie breaks: a
 * cleared site-data, a private window, a device that dropped the cookie, a
 * browser that expired it early. The user never touched anything they would
 * describe as "signing out", so being asked for a password again reads as the
 * product forgetting them.
 *
 * ── The cost of (2), stated plainly ─────────────────────────────────────────
 * A token in `localStorage` is readable by any script that runs on this origin
 * and signs that script in as you HERE. It is not your password: it opens
 * nothing on any other site, and bumping the account's `tokenVersion` kills it.
 * "Keep me signed in" unticked stores nothing, and signing out erases it.
 */

import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
  type ReactNode,
} from 'react';
import { DEFAULT_COMPANY } from './company';
import {
  fetchAuth, loginAccount, logoutAccount, registerAccount, saveFlagsOff, updateAccount,
  migrateLegacyLogin, setKeepSignedIn, readToken, rememberToken,
  setGuestMode, GUEST_REMAINING_EVENT,
  MARQ_BALANCE_EVENT, claimDailyMarq, fetchMarq, type MarqWallet, type DailyClaimResult,
  type Account, type AuthState, type GuestCredits, type Gender,
} from './api';

/**
 * The browser's copy of a guest's free-translation count.
 *
 * The SERVER holds the real count (per IP, backend lib/guestCredits.ts) — a
 * count that lived only here would reset with "clear site data". This copy is
 * what the header chip shows before /api/auth/me answers, so the number does
 * not flicker in on every load.
 */
const GUEST_STORE = 'gt_guest_credits';

export function readGuestCopy(): GuestCredits | null {
  try {
    const raw = localStorage.getItem(GUEST_STORE);
    const v = raw ? JSON.parse(raw) : null;
    return v && typeof v.total === 'number' && typeof v.remaining === 'number' ? v : null;
  } catch {
    return null;
  }
}

function writeGuestCopy(g: GuestCredits | null): void {
  try {
    if (g) localStorage.setItem(GUEST_STORE, JSON.stringify(g));
    else localStorage.removeItem(GUEST_STORE);
  } catch { /* storage disabled — the server's count is the real one anyway */ }
}

/**
 * A guest's hidden-flags list lives in the browser, not on a server.
 *
 * An account keeps `flagsOff` on the account so it follows the person between
 * devices; a guest has no account to keep it on, so this is the honest place
 * for it — per browser, and gone when they clear site data. The Flags tab says
 * as much. Unlike the free-translation count there is no money in it, so there
 * is no reason to anchor it server-side.
 */
const GUEST_FLAGS_STORE = 'gt_guest_flags';

function readGuestFlags(): string[] {
  try {
    const raw = localStorage.getItem(GUEST_FLAGS_STORE);
    const v = raw ? JSON.parse(raw) : null;
    return Array.isArray(v) ? v.filter((c) => typeof c === 'string') : [];
  } catch {
    return [];
  }
}

function writeGuestFlags(flagsOff: string[]): void {
  try { localStorage.setItem(GUEST_FLAGS_STORE, JSON.stringify(flagsOff)); }
  catch { /* storage disabled — the switches still work for this session */ }
}

/*
 * What this browser keeps to stay signed in is the server's TOKEN, never the
 * password — lib/api.ts (`gt_token`, `migrateLegacyLogin`) owns it, because the
 * fetch layer is what presents it. Re-exported for the login form's pre-fill.
 */
export { rememberedNickname } from './api';

export interface Auth {
  /** The first answer has arrived. Nothing should be decided before this. */
  loaded: boolean;
  /** Includes the automatic sign-in attempt — see the effect below. */
  user: Account | null;
  teams: number[];
  maxNickname: number;
  maxCompany: number;
  /**
   * What the sign-up form puts in the company box before anybody types.
   *
   * From the server. A suggestion and not a rule — the form sends whatever is
   * in the box, and the server refuses a registration that carries nothing.
   */
  defaultCompany: string;
  minPassword: number;
  /** The backend could not be reached; do not conclude anything from `user`. */
  unavailable: boolean;
  /**
   * Free translations for a signed-out browser; null for an account.
   * `total: 0` = guest mode off, the translator needs a sign-in.
   */
  guest: GuestCredits | null;
  /**
   * The KHARMA wallet — balance, and whether today's reward is still there.
   * Null for a signed-out browser (a guest has free translations instead) and
   * when KHARMA is switched off server-side.
   *
   * Arrives with `/auth/me` so the header draws the balance on first paint
   * rather than flashing an empty chip, and is kept current afterwards by the
   * `X-Marq-Balance` header every spend returns — see lib/api.ts.
   */
  marq: MarqWallet | null;
  /** The exact server receipt. Repeated claims return claimed:false; failures throw. */
  claimDaily: () => Promise<DailyClaimResult>;
  refreshWallet: () => Promise<void>;
  signIn: (nickname: string, password: string, remember: boolean) => Promise<Account>;
  /** Nickname, password, gender — the whole sign-up. */
  signUp: (nickname: string, password: string, gender: Gender, remember: boolean) => Promise<Account>;
  /** "Join with Reddit" is configured on this server. */
  reddit: boolean;
  signOut: () => Promise<void>;
  /** Nickname, company and/or team, straight onto the account. */
  update: (patch: { nickname?: string; team?: number; company?: string; gender?: Gender }) => Promise<Account>;
  /**
   * Country flags this person hides — Settings ▸ Flags, and read by every
   * flag on the page through `useFlagVisible` in components/FlagIcon.
   *
   * Empty for a signed-out browser and for a page with no provider over it
   * (the admin console is one), which is the right answer in both: nobody has
   * expressed a preference, so nothing is hidden.
   */
  flagsOff: string[];
  /**
   * Replace that list. Applied on screen immediately and rolled back if the
   * write fails — see the implementation for why that way round.
   */
  setFlagsOff: (flagsOff: string[]) => Promise<void>;
}

const FALLBACK: Auth = {
  loaded: false,
  user: null,
  teams: [0, 1, 2, 3, 4],
  maxNickname: 40,
  maxCompany: 24,
  defaultCompany: DEFAULT_COMPANY,
  minPassword: 8,
  reddit: false,
  unavailable: false,
  guest: null,
  marq: null,
  claimDaily: async () => { throw new Error('Sign in to collect your reward.'); },
  refreshWallet: async () => {},
  signIn: async () => { throw new Error('Sign-in is not available on this page.'); },
  signUp: async () => { throw new Error('Sign-up is not available on this page.'); },
  signOut: async () => {},
  update: async () => { throw new Error('Sign-in is not available on this page.'); },
  flagsOff: [],
  setFlagsOff: async () => { throw new Error('Sign-in is not available on this page.'); },
};

const Ctx = createContext<Auth>(FALLBACK);

export function useAuth(): Auth {
  return useContext(Ctx);
}

/**
 * Mounted on the pages that have a user — the translator, and the two forms
 * themselves. NOT in the root layout, for the reason app/layout.tsx and
 * IdentityGate both give at length: that layout also wraps the admin console,
 * which authenticates with its own key and must never be sent to a login page
 * built for translator users.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState | null>(null);
  const [loaded, setLoaded] = useState(false);
  /** Guards the automatic attempt, so React 18's double-invoked effect in dev
      cannot fire two logins with the same credentials. */
  const tried = useRef(false);
  /** A guest's hidden flags, mirrored from localStorage after mount. Empty on
      the server render so it never differs between SSR and hydration. */
  const [guestFlags, setGuestFlags] = useState<string[]>([]);
  useEffect(() => { setGuestFlags(readGuestFlags()); }, []);

  useEffect(() => {
    const ac = new AbortController();

    (async () => {
      const first = await fetchAuth(ac.signal);
      if (ac.signal.aborted) return;

      /*
       * THE AUTOMATIC SIGN-IN is fetchAuth itself: it presents the stored token,
       * and the server signs this browser in and sets the cookie again.
       *
       * What is left here is the one-time move off the OLD password copy
       * (`gt_login`): signed in → it is just erased; not signed in → it is sent
       * one last time, traded for a token, and erased. Silent either way — a
       * password that no longer works is not an error for somebody who never
       * typed anything; the login page does its job.
       */
      if (!first.unavailable && !tried.current) {
        tried.current = true;
        const ok = await migrateLegacyLogin(!!first.user);
        if (ok && !first.user) {
          const again = await fetchAuth(ac.signal);
          if (ac.signal.aborted) return;
          setState(again);
          setLoaded(true);
          return;
        }
      }

      if (ac.signal.aborted) return;
      setState(first);
      setLoaded(true);
    })();

    return () => ac.abort();
  }, []);

  const signIn = useCallback(async (nickname: string, password: string, remember: boolean) => {
    // "Keep me signed in" = a 30-day refresh cookie; off = gone when the browser closes (server-side, lib/api.ts).
    setKeepSignedIn(remember);
    const user = await loginAccount(nickname, password, remember);
    setState((s) => (s ? { ...s, user, unavailable: false } : s));
    return user;
  }, []);

  const signUp = useCallback(
    async (nickname: string, password: string, gender: Gender, remember: boolean) => {
      setKeepSignedIn(remember);
      const user = await registerAccount(nickname, password, gender, remember);
      setState((s) => (s ? { ...s, user, unavailable: false } : s));
      return user;
    },
    [],
  );

  // Guest bookkeeping: tell the API layer whether to prompt instead of
  // redirecting, keep the browser's copy, and follow the server's count.
  const guest = state && !state.user ? state.guest : null;
  useEffect(() => {
    setGuestMode(!!guest && guest.total > 0);
    if (state && !state.unavailable) writeGuestCopy(state.user ? null : guest);
  }, [state, guest]);
  useEffect(() => {
    const onRemaining = (e: Event) => {
      const left = Number((e as CustomEvent).detail);
      if (!Number.isFinite(left)) return;
      setState((s) =>
        s && !s.user && s.guest
          ? { ...s, guest: { ...s.guest, remaining: left, used: Math.max(0, s.guest.total - left) } }
          : s,
      );
    };
    /*
     * Then re-read the server's count a moment later. The header value is the
     * count at the moment of charging, but a cache hit or a same-language
     * no-op is REFUNDED after the stream — so without this the chip would
     * show one fewer than the guest really has.
     */
    let resync: ReturnType<typeof setTimeout> | null = null;
    const onAny = (e: Event) => {
      onRemaining(e);
      if (resync) clearTimeout(resync);
      resync = setTimeout(() => {
        void fetchAuth().then((fresh) => {
          if (fresh.unavailable || fresh.user) return;
          setState((s) => (s && !s.user ? { ...s, guest: fresh.guest } : s));
        });
      }, 2500);
    };
    window.addEventListener(GUEST_REMAINING_EVENT, onAny);
    return () => {
      window.removeEventListener(GUEST_REMAINING_EVENT, onAny);
      if (resync) clearTimeout(resync);
    };
  }, []);

  /*
   * The KHARMA balance, kept current from whichever call just spent some.
   *
   * Every spend answers with `X-Marq-Balance` and lib/api.ts turns that into an
   * event, so the header never has to poll and no component between it and the
   * translator has to carry a balance it has no use for. Mirrors the guest
   * listener above — the difference is that there is no resync timer, because
   * KHARMA is refunded inside the same request that failed rather than after the
   * stream has closed, so the header value is already the settled one.
   */
  useEffect(() => {
    const onBalance = (e: Event) => {
      const balance = Number((e as CustomEvent).detail);
      if (!Number.isFinite(balance)) return;
      setState((prev) =>
        prev && prev.marq ? { ...prev, marq: { ...prev.marq, balance } } : prev,
      );
    };
    window.addEventListener(MARQ_BALANCE_EVENT, onBalance);
    return () => window.removeEventListener(MARQ_BALANCE_EVENT, onBalance);
  }, []);

  const walletUserId = state?.user?.id;
  const walletSequence = useRef(0);
  const refreshWallet = useCallback(async () => {
    if (!walletUserId) return;
    const sequence = ++walletSequence.current;
    const wallet = await fetchMarq(false);
    if (sequence !== walletSequence.current) return;
    setState((prev) => prev?.user?.id === walletUserId ? { ...prev, marq: wallet } : prev);
  }, [walletUserId]);

  const pendingClaim = useRef<{ userId: string; promise: Promise<DailyClaimResult> } | null>(null);
  const claimDaily = useCallback((): Promise<DailyClaimResult> => {
    if (!walletUserId) return Promise.reject(new Error('Sign in to collect your reward.'));
    if (pendingClaim.current?.userId === walletUserId) return pendingClaim.current.promise;
    // Invalidate older reads so they cannot replace a freshly claimed wallet.
    ++walletSequence.current;
    const promise = claimDailyMarq().then((result) => {
      ++walletSequence.current;
      setState((prev) => prev?.user?.id === walletUserId ? { ...prev, marq: result.wallet } : prev);
      try { localStorage.setItem(`tm:wallet:${walletUserId}`, `${Date.now()}:${Math.random()}`); } catch { /* storage restricted */ }
      return result;
    }).catch(async (error) => {
      await refreshWallet().catch(() => undefined);
      throw error;
    }).finally(() => { if (pendingClaim.current?.promise === promise) pendingClaim.current = null; });
    pendingClaim.current = { userId: walletUserId, promise };
    return promise;
  }, [walletUserId, refreshWallet]);

  const resetAt = state?.marq?.nextDailyAt;
  useEffect(() => {
    if (!walletUserId) return;
    const refresh = () => { if (!document.hidden) void refreshWallet().catch(() => undefined); };
    const onStorage = (event: StorageEvent) => { if (event.key === `tm:wallet:${walletUserId}`) refresh(); };
    const timer = setInterval(() => { if (resetAt && Date.now() >= Date.parse(resetAt)) refresh(); }, 15_000);
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('storage', onStorage);
    return () => {
      clearInterval(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('storage', onStorage);
    };
  }, [walletUserId, resetAt, refreshWallet]);

  const signOut = useCallback(async () => {
    // The cookie AND the stored token (logoutAccount erases it): one left behind
    // would sign the person straight back in on the next page load.
    await logoutAccount();
    setState((s) => (s ? { ...s, user: null } : s));
  }, []);

  const update = useCallback(
    async (patch: { nickname?: string; team?: number; company?: string; gender?: Gender }) => {
      const user = await updateAccount(patch);
      // The sign-in cookies do not depend on the nickname; only the login form's pre-fill does.
      rememberToken(null, user.nickname);
      setState((s) => (s ? { ...s, user } : s));
      return user;
    },
    [],
  );

  /**
   * Writes to /auth/flags, in the order they were made, one at a time.
   *
   * The route replaces the WHOLE list, so two overlapping writes are not two
   * edits — they are two complete answers, and the wrong one arriving last is
   * the settings dialog silently undoing a switch somebody just flipped. A
   * chain costs one queued promise and removes the question.
   */
  const writes = useRef<Promise<unknown>>(Promise.resolve());
  /** Which write is the current one; older replies are ignored, not applied. */
  const writeSeq = useRef(0);

  /**
   * The flag list, written to the screen first and to the server second.
   *
   * OPTIMISTIC, and deliberately so. This is a switch: the whole of what it
   * promises is that the flag stops being drawn, and a switch that waits for a
   * round trip before moving is a switch people press twice. The rollback is
   * what keeps that honest — a failed write puts the previous list back, so the
   * dialog cannot end up showing a setting the account does not have.
   *
   * `previous` is read from the rendered state rather than from inside the
   * updater, which costs this callback a new identity per change and buys
   * correctness: React may run an updater during a later render, so a rollback
   * value captured in there can still be unset by the time the request fails.
   *
   * Both the confirmation and the rollback are gated on being the LATEST write.
   * Without that, the server's answer to the first of two quick toggles lands
   * after the second one and repaints the older list — a flag that flickers
   * back on for as long as the second request takes.
   */
  const setFlagsOff = useCallback(async (flagsOff: string[]) => {
    const current = state?.user;
    // A guest: kept in this browser, applied at once, no request to make or
    // roll back. See GUEST_FLAGS_STORE.
    if (!current) {
      setGuestFlags(flagsOff);
      writeGuestFlags(flagsOff);
      return;
    }
    const previous = current.flagsOff ?? [];
    const mine = ++writeSeq.current;

    setState((s) => (s?.user ? { ...s, user: { ...s.user, flagsOff } } : s));

    // Both arms run the same write: a failed predecessor must not cancel the
    // writes queued behind it, only delay them.
    const run = writes.current.then(
      () => saveFlagsOff(flagsOff),
      () => saveFlagsOff(flagsOff),
    );
    writes.current = run.catch(() => {});

    try {
      const user = await run;
      if (writeSeq.current === mine) setState((s) => (s?.user ? { ...s, user } : s));
    } catch (err) {
      if (writeSeq.current === mine) {
        setState((s) => (s?.user ? { ...s, user: { ...s.user, flagsOff: previous } } : s));
      }
      throw err;
    }
  }, [state]);

  const value = useMemo<Auth>(() => ({
    loaded,
    user: state?.user ?? null,
    teams: state?.teams?.length ? state.teams : FALLBACK.teams,
    maxNickname: state?.maxNickname ?? FALLBACK.maxNickname,
    maxCompany: state?.maxCompany ?? FALLBACK.maxCompany,
    defaultCompany: state?.defaultCompany || FALLBACK.defaultCompany,
    minPassword: state?.minPassword ?? FALLBACK.minPassword,
    reddit: state?.reddit ?? false,
    unavailable: !!state?.unavailable,
    guest: state && !state.user ? state.guest ?? null : null,
    // An account's wallet only — a signed-out browser has guest credits instead.
    marq: state?.user ? state.marq ?? null : null,
    claimDaily, refreshWallet,
    // An account's flags live on the account; a guest's live in this browser.
    flagsOff: state?.user?.flagsOff ?? guestFlags,
    signIn, signUp, signOut, update, setFlagsOff,
  }), [loaded, state, guestFlags, signIn, signUp, signOut, update, setFlagsOff, claimDaily, refreshWallet]);

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
